import { FC, useState, useCallback, useMemo } from 'react';
import { Box, Typography, Stack, Skeleton, Chip, IconButton, Tooltip } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNoteFormats } from '../hooks/useNoteFormats';
import FormatTypeSelector from './FormatTypeSelector';
import useNoteFormatDisplayStore from '@/stores/noteFormatDisplayStore';

interface FormatsTabContentProps {
  noteId: string;
}

const FormatsTabContent: FC<FormatsTabContentProps> = ({ noteId }) => {
  const {
    formatTypes,
    isLoadingTypes,
    generatedFormats,
    isLoadingFormats,
    isGenerating,
    generate,
    deleteFormat,
    selectFormat,
  } = useNoteFormats(noteId);

  const activeTab = useNoteFormatDisplayStore((s) => s.activeTab);
  const setActiveTab = useNoteFormatDisplayStore((s) => s.setActiveTab);

  const [generatingPromptId, setGeneratingPromptId] = useState<string | null>(null);

  const handleGenerate = useCallback(
    async (promptId: string) => {
      setGeneratingPromptId(promptId);
      try {
        await generate(promptId);
      } finally {
        setGeneratingPromptId(null);
      }
    },
    [generate],
  );

  const getDisplayName = (format: { prompt_value: string }) => {
    const match = formatTypes.find((t) => t.prompt_value === format.prompt_value);
    return match?.display_name || format.prompt_value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Filter out format types that already have a generated result
  const generatedPromptValues = useMemo(() => new Set(generatedFormats.map((f) => f.prompt_value)), [generatedFormats]);
  const availableFormatTypes = useMemo(
    () => formatTypes.filter((t) => !generatedPromptValues.has(t.prompt_value)),
    [formatTypes, generatedPromptValues],
  );

  // Loading state
  if (isLoadingTypes || isLoadingFormats) {
    return (
      <Stack spacing={2} sx={{ py: 2 }}>
        <Skeleton variant="rounded" height={44} />
        <Skeleton variant="rounded" height={44} />
        <Skeleton variant="rounded" height={44} />
      </Stack>
    );
  }

  // Nothing generated and no live format types — say so instead of rendering a blank panel
  if (generatedFormats.length === 0 && availableFormatTypes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
        No formats are available right now.
      </Typography>
    );
  }

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      {/* Previously generated formats */}
      {generatedFormats.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ px: 0.5 }}>
            Generated formats
          </Typography>
          {generatedFormats.map((format) => {
            const isActive = activeTab === format.id;
            return (
              <Box
                key={format.id}
                onClick={() => {
                  if (isActive) {
                    selectFormat(null);
                    setActiveTab('original');
                  } else {
                    selectFormat(format);
                    setActiveTab(format.id);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: 1,
                  borderColor: isActive ? 'primary.main' : 'divider',
                  bgcolor: isActive ? 'action.selected' : 'background.paper',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: isActive ? 'action.selected' : 'action.hover',
                  },
                }}
              >
                <DescriptionOutlinedIcon sx={{ fontSize: 20, color: isActive ? 'primary.main' : 'text.secondary' }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {getDisplayName(format)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(format.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Chip
                  label={isActive ? 'Viewing' : 'View'}
                  size="small"
                  variant={isActive ? 'filled' : 'outlined'}
                  color={isActive ? 'primary' : 'default'}
                  sx={{ fontSize: '0.7rem' }}
                />
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteFormat(format.id);
                    }}
                    sx={{ ml: 0.5, p: 0.5 }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Stack>
      )}

      {/* Generate new format section — exclude types that already have a generated result */}
      <FormatTypeSelector
        formatTypes={availableFormatTypes}
        isGenerating={isGenerating}
        generatingPromptId={generatingPromptId}
        onSelect={handleGenerate}
      />
    </Stack>
  );
};

export default FormatsTabContent;
